import { Alert } from '@creditornot/cb-alert';
import { Spinner } from '@creditornot/cb-progress';
import { useQuery } from '@tanstack/react-query';
import React, { ReactNode, useEffect } from 'react';
import { useIntl } from 'react-intl';
import { Redirect, Route, Switch, useHistory, useRouteMatch } from 'react-router-dom';
import styled from 'styled-components';

import useUser from 'common/hooks/useUser';
import PageLoadError from 'core/PageLoadError';

import OnboardingPageHeader from './components/OnboardingPageHeader';
import StepsPreview from './components/StepsPreview/StepsPreview';
import SuccessView from './components/SuccessView';
import { SsmoTask } from './constants';
import ContractInfoWizardPage from './pages/ContractInfoWizardPage';
import { KybPage } from './pages/KybPage';
import { KycPage } from './pages/KycPage';
import OpeningTimesPage from './pages/OpeningTimesPage';
import SignContractPage from './pages/SignContractPage';
import UploadMenuPage from './pages/UploadMenuPage';
import { getDisabledTasks } from './utils';
import {
  getCountriesForFeature,
  getOnboardingProgress,
  getOnboardingStatus,
} from '../../apis/merchant-onboarding-service/api';
import { useIsMobile } from '../../common/hooks/useIsMobile';
import ComplianceFooter from '../self-serve-sign-up/ComplianceFooter';

type RouteWithRedirectProps = {
  path: string;
  redirectCondition: boolean;
  redirectTo: string;
  component: ReactNode;
  exact?: boolean;
};

const RouteWithRedirect = ({ path, redirectCondition, redirectTo, component: component }: RouteWithRedirectProps) => (
  <Route path={path} render={() => (redirectCondition ? <Redirect to={redirectTo} /> : component)} />
);

const Root = styled.div`
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  align-items: center;
`;

const OnboardingPage = ({
  setSkipIDVerification,
}: {
  setSkipIDVerification: (skipIDVerification: boolean) => void;
}) => {
  const history = useHistory();
  const { formatMessage } = useIntl();
  const { user } = useUser();
  const { url } = useRouteMatch();
  const isMobile = useIsMobile();

  const { data: enabledCountries = [], isFetching: isFetchingEnabledCountries } = useQuery(
    ['/merchant-admin/config/features', 'ssmo-onboarding-page'],
    () => getCountriesForFeature('ssmo-onboarding-page'),
    {
      refetchOnWindowFocus: false,
    },
  );

  const {
    data: onboardingStatusData,
    isLoading: isFetchingOnboardingStatus,
    error: onboardingStatusError,
  } = useQuery(['merchant-admin/me/onboarding-status'], () => getOnboardingStatus());

  const merchantId = onboardingStatusData?.merchantId;

  const {
    data: onboardingProgress,
    error: onboardingProgressError,
    isLoading: isLoadingOnboardingProgress,
    refetch: refetchOnboardingProgress,
  } = useQuery(
    [`/merchant-admin/inbound-merchants/${merchantId}/onboarding-progress`],
    () => (merchantId ? getOnboardingProgress(merchantId) : Promise.reject('merchantId is undefined')),
    {
      enabled: Boolean(merchantId),
    },
  );

  useEffect(() => {
    if (history.location.pathname) {
      refetchOnboardingProgress();
    }
  }, [history.location.pathname, refetchOnboardingProgress]);

  const isSsmoOnboardingPageEnabled = onboardingStatusData?.country
    ? enabledCountries.includes(onboardingStatusData?.country)
    : false;

  const isLoading = isFetchingOnboardingStatus || isFetchingEnabledCountries || isLoadingOnboardingProgress;

  if (isLoading) {
    return (
      <Root>
        <Spinner size="large" />
      </Root>
    );
  }
  if (!merchantId || !user) {
    return <PageLoadError />;
  }

  if (onboardingProgressError || !onboardingProgress || onboardingStatusError) {
    return (
      <Root>
        <Alert variant="error" title={formatMessage({ id: 'self-serve-sign-up.error-unknown' })} />
      </Root>
    );
  }

  const disabledTasks = getDisabledTasks(onboardingProgress);

  return (
    <Root>
      <Switch>
        <Route exact path={url}>
          <OnboardingPageHeader />

          {isSsmoOnboardingPageEnabled ? (
            <StepsPreview
              merchantId={merchantId}
              firstName={user.name?.first_name}
              onboardingProgress={onboardingProgress}
            />
          ) : (
            <SuccessView merchantId={merchantId} firstName={user.name?.first_name} />
          )}
        </Route>
        <RouteWithRedirect
          exact
          path={`${url}/opening-hours`}
          redirectCondition={disabledTasks.has(SsmoTask.OPENING_TIMES)}
          redirectTo={url}
          component={<OpeningTimesPage merchantId={merchantId} />}
        />
        <RouteWithRedirect
          exact
          path={`${url}/contract-details`}
          redirectCondition={disabledTasks.has(SsmoTask.CONTRACT_SIGNING)}
          redirectTo={url}
          component={<ContractInfoWizardPage merchantId={merchantId} onboardingStatus={onboardingStatusData.status} />}
        />
        <RouteWithRedirect
          exact
          path={`${url}/upload-menu`}
          redirectCondition={disabledTasks.has(SsmoTask.MENU_SUBMISSION)}
          redirectTo={url}
          component={<UploadMenuPage merchantId={merchantId} />}
        />
        <RouteWithRedirect
          path={`${url}/identity-verification`}
          redirectCondition={disabledTasks.has(SsmoTask.KYC)}
          redirectTo={url}
          component={<KycPage setSkipIDVerification={setSkipIDVerification} />}
        />
        <RouteWithRedirect
          path={`${url}/:slug/business-verification`}
          redirectCondition={disabledTasks.has(SsmoTask.KYB)}
          redirectTo={url}
          component={<KybPage />}
        />
        <Route exact path={`${url}/sign-contract`}>
          <SignContractPage merchantId={merchantId} />
        </Route>
      </Switch>
      <ComplianceFooter isMobile={isMobile} showContactUs showUserTerms showPrivacyPolicy />
    </Root>
  );
};

export default OnboardingPage;
